import { toPublicShop } from '@getjust/gateway';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import axios from 'axios';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import type { CheckoutV1 } from '@getjust/api-schema';
import type { GetServerSidePropsContext, InferGetServerSidePropsType, NextPage } from 'next';

import { useCustomRouter } from '$hooks/client';
import { SHOP_QUERY_KEY } from '$hooks/queries';
import { AuthStatuses, useAuth } from '$src/hooks/auth';
import { useLoadingAtom } from '$src/hooks/state/useLoadingAtom';
import { type PostHogBootstrapData, getPostHogBootstrapData } from '$src/utils/getPostHogBootstrapData';

const CACHE_DURATION_IN_SECONDS = 60;

/**
 * Drop `undefined` properties recursively. `toPublicShop` builds an allowlist literal that
 * leaves absent fields as `undefined`; Next's getServerSideProps serializer rejects `undefined`
 * (unlike JSON, which omits it — what the client `/api/{shopId}` route does via `res.json`).
 */
function pruneUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => pruneUndefined(item)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, val]) => val !== undefined)
        .map(([key, val]) => [key, pruneUndefined(val)]),
    ) as T;
  }
  return value;
}

export const getServerSideProps = async ({ res, req, locale = 'fr', query }: GetServerSidePropsContext) => {
  res.setHeader(
    'Cache-Control',
    `public, s-maxage=${CACHE_DURATION_IN_SECONDS}, stale-while-revalidate=${CACHE_DURATION_IN_SECONDS - 1}`,
  );

  let postHogBootstrapData: PostHogBootstrapData | null = null;
  const bootstrapDistinctId = z.string().safeParse(query?.uid).data;

  try {
    postHogBootstrapData = await getPostHogBootstrapData({
      bootstrapDistinctId,
      requestHeaders: req.headers,
    });
  } catch {
    console.error('Failed to get posthog bootstrap data');
  }

  const shopId = z.string().safeParse(query?.shopId).data;
  if (!shopId) {
    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        postHogBootstrapData,
      },
    };
  }

  const headers = {
    'x-request-id': uuidv4(),
    'getjust-gateway-token': process.env.GETJUST_API_TOKEN,
  };

  const url = process.env.NEXT_PUBLIC_JUST_API_URL + `/v1/shop/${shopId}`;

  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: [SHOP_QUERY_KEY],
    // SECURITY: the raw backend shop carries credentials (shopify accessToken/secretKey/apiKey/
    // storefrontToken, prestashop apiKey, settings.pickup carrier tokens, integrations[].credentials).
    // getServerSideProps props — including this dehydrated cache — are serialized verbatim into
    // __NEXT_DATA__, so we MUST project to the public secret-free shape before dehydrating.
    // The raw shop stays server-side only; this matches the shape the client gets from /api/{shopId}.
    queryFn: async () => {
      const { data } = await axios.get<CheckoutV1.ShopGetShopResponseBody>(url, { headers });
      return pruneUndefined(toPublicShop(data));
    },
  });

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      shopId,
      dehydratedState: dehydrate(queryClient),
      postHogBootstrapData,
    },
  };
};

/**
 * This index page is used like a router, redirecting to the correct page to display
 */
const CheckoutForm: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = () => {
  const router = useCustomRouter();
  const { authState } = useAuth() ?? {};
  const { onLoadingChange } = useLoadingAtom();

  useEffect(() => {
    if (authState === AuthStatuses.PENDING) return;
    router.push(`/${authState}`);
  }, [authState, router]);

  useEffect(() => {
    onLoadingChange(authState === AuthStatuses.PENDING);
  }, [onLoadingChange, authState]);

  return <></>;
};

export default CheckoutForm;
